/**
 * 控制面 REST／WS 客戶端（瀏覽器帶 vm-auth session）。
 */
import { getPinToken, getVmSessionId, vmSessionHeaders } from '../auth/session';
import { API_BASE, IS_MOCK } from '../api';
import { mockFleetRequest, subscribeFleetMock, type FleetMessage, type FleetSession } from './fleetMock';
import { parseFleetNodes, type FleetNode } from './fleetNodes';
import { readRoomMeta } from './roomMeta';

function authHeaders(): HeadersInit {
  return { accept: 'application/json', 'content-type': 'application/json', ...vmSessionHeaders() };
}

/** 本機 dev 沒設 VITE_API_BASE 時 API_BASE 是空的。相對路徑會打到 Vite，控制頁就冒 404。 */
function fleetMockActive(): boolean {
  return IS_MOCK || API_BASE.trim() === '';
}

export class FleetHttpError extends Error {
  readonly status: number;
  readonly code: string | null;

  constructor(status: number, message: string, code: string | null) {
    super(message);
    this.name = 'FleetHttpError';
    this.status = status;
    this.code = code;
  }
}

async function fleetFetch<T>(path: string, init?: RequestInit): Promise<T> {
  if (fleetMockActive()) return mockFleetRequest<T>(path, init);
  // 認證靠 Authorization／x-vm-session 標頭（見 session.ts）；不要 credentials:include——
  // Worker CORS 不回 Access-Control-Allow-Credentials，瀏覽器會整段變成 Failed to fetch。
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { ...authHeaders(), ...(init?.headers ?? {}) },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; code?: string };
    const code = typeof body.code === 'string' && body.code !== '' ? body.code : null;
    throw new FleetHttpError(res.status, body.error ?? `${res.status} ${path}`, code);
  }
  return (await res.json()) as T;
}

export type { FleetMessage, FleetSession };

export function listFleetSessions(query: { mode?: string; platform?: string; life?: string } = {}) {
  const q = new URLSearchParams();
  if (query.mode) q.set('mode', query.mode);
  if (query.platform) q.set('platform', query.platform);
  if (query.life) q.set('life', query.life);
  const suffix = q.toString() ? `?${q}` : '';
  return fleetFetch<{ sessions: FleetSession[] }>(`/api/fleet/sessions${suffix}`);
}

export type FleetOutcomeRow = { event: string; occurredAt: string };

export async function listFleetMessages(query: { since?: string; sessionId?: string; limit?: number } = {}) {
  const q = new URLSearchParams();
  if (query.since) q.set('since', query.since);
  if (query.sessionId) q.set('sessionId', query.sessionId);
  if (query.limit) q.set('limit', String(query.limit));
  const suffix = q.toString() ? `?${q}` : '';
  const payload = await fleetFetch<{ messages: FleetMessage[]; outcomes?: FleetOutcomeRow[] }>(
    `/api/fleet/messages${suffix}`,
  );
  const messages = payload.messages.map((message) => {
    const meta = readRoomMeta(message.meta);
    return meta ? { ...message, meta } : { ...message, meta: undefined };
  });
  if (!payload.outcomes) return { messages };
  return { messages, outcomes: payload.outcomes };
}

export function setSessionMode(sessionId: string, mode: 'auto' | 'human', platform = 'wootalk') {
  return fleetFetch<{ ok: boolean; mode: string }>(`/api/fleet/sessions/${encodeURIComponent(sessionId)}/mode`, {
    method: 'PATCH',
    body: JSON.stringify({ mode, platform }),
  });
}

export function sayAsHuman(sessionId: string, text: string, platform = 'wootalk') {
  return fleetFetch<{ ok: boolean; id: string }>(`/api/fleet/sessions/${encodeURIComponent(sessionId)}/say`, {
    method: 'POST',
    body: JSON.stringify({ text, platform }),
  });
}

export function getFleetConfig() {
  return fleetFetch<{
    unchanged?: boolean;
    version: number;
    params?: Record<string, unknown>;
    updatedAt?: string;
    updatedBy?: string | null;
  }>('/api/fleet/config?since=0');
}

export function putFleetParams(params: Record<string, unknown>) {
  return fleetFetch<{ ok: boolean; version: number }>('/api/fleet/config', {
    method: 'PUT',
    body: JSON.stringify({ params }),
  });
}

export type { FleetNode };

export async function listFleetNodes(): Promise<FleetNode[]> {
  const payload = await fleetFetch<unknown>('/api/fleet/nodes');
  return parseFleetNodes(payload, Date.now());
}

export function getMonitor(hours = 24) {
  return fleetFetch<{
    hours: number;
    outcomes: Array<{ event: string; n: number }>;
    byPlatform?: unknown;
    modes: Array<{ mode: string; n: number }>;
    humanOpen: number;
    messages: number;
    paramsVersion: number;
    params: Record<string, unknown>;
    nodesAlive: number;
  }>(`/api/fleet/monitor?hours=${hours}`);
}

export function listGrants() {
  return fleetFetch<{
    grants: Array<{
      id: string;
      operatorEmail: string;
      platform: string;
      persona: string;
      canTakeover: number;
      canSend: number;
    }>;
  }>('/api/fleet/grants');
}

export function createGrant(input: {
  operatorEmail: string;
  platform?: string;
  persona?: string;
  canTakeover?: boolean;
  canSend?: boolean;
}) {
  return fleetFetch<{ ok: boolean; id: string }>('/api/fleet/grants', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function deleteGrant(id: string) {
  return fleetFetch<{ ok: boolean }>(`/api/fleet/grants?id=${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export type FleetSocket = { close(): void };

/** 訊息瀑布推送。mock 沒有後端 socket，改訂閱本機 inject，失敗時呼叫端仍靠輪詢。 */
export function openFleetWs(onEvent: (data: unknown) => void): FleetSocket | null {
  if (fleetMockActive()) {
    const stop = subscribeFleetMock(onEvent);
    return { close: stop };
  }
  try {
    const token = getPinToken() || getVmSessionId() || '';
    const base = API_BASE.replace(/^http/, 'ws');
    const url = `${base}/api/fleet/ws${token ? `?token=${encodeURIComponent(token)}` : ''}`;
    const ws = new WebSocket(url);
    ws.onmessage = (ev) => {
      try {
        onEvent(JSON.parse(String(ev.data)));
      } catch {
        /* ignore */
      }
    };
    return ws;
  } catch {
    return null;
  }
}
